/**
 * Client STOMP/SockJS partagé du front (UPT-1814, lot sécurité — partie client).
 *
 * Un seul point de code pour la connexion temps réel : job_notifications.ts
 * (notifications de jobs) et dashboardContextBus.ts (contexte dashboard)
 * gardent chacun leur client et leurs abonnements, mais l'URL, les transports,
 * les EN-TÊTES DU CONNECT et la politique de reconnexion sont fabriqués ici.
 *
 * Contrat serveur (UPT-1814) :
 * - le CONNECT porte l'en-tête natif `Authorization: Bearer <JWT Clerk>` — le
 *   MÊME jeton que les appels REST : chaîne clerkTokenProvider →
 *   getPreferredAccessToken (cf. l'intercepteur axios de ClientProviders),
 *   via ensureFreshAccessToken pour ne pas partir avec un jeton sur le point
 *   d'expirer. Le jeton Clerk expire ~60 s : il n'est JAMAIS mis en cache ici,
 *   il est obtenu de façon asynchrone dans `beforeConnect`, AVANT chaque
 *   (re)connexion ;
 * - sans jeton valide le serveur refuse le CONNECT (trame ERROR) ; un
 *   abonnement non autorisé est refusé de la même façon après CONNECTED : on
 *   l'écrit en console, puis on se reconnecte avec un jeton frais après un
 *   délai CROISSANT (5 s → 60 s, cf. reconnectDelayMs). Ce délai est piloté
 *   ici et non par le backoff natif de stompjs, qui repart de 5 s à chaque
 *   CONNECTED — un abonnement refusé en boucle ne serait sinon jamais espacé.
 *   Au-delà de MAX_SERVER_REFUSALS refus consécutifs on abandonne pour la
 *   session ;
 * - sans jeton disponible (visiteur non connecté, Clerk pas encore prêt) :
 *   AUCUN CONNECT anonyme n'est envoyé (refus garanti) — le client se met en
 *   retrait et réessaie après un délai croissant ; un nouvel appel à
 *   ensureConnected() (job lancé, page dashboard) relance l'essai tout de suite ;
 * - endpoint injoignable (UPT-1512, ex. proxy SWA sans route /ws) : budget de
 *   MAX_CONNECT_FAILURES échecs consécutifs puis abandon pour la session ;
 * - seules les destinations `/topic/dashboard/{tenantId}/{workspaceId}/context`
 *   et `/topic/jobs/{tenantId}` sont autorisées par le serveur : elles sont
 *   construites par jobsTopic() / dashboardContextTopic(), rien d'autre.
 *
 * Hébergement : backendBaseUrl() coerce http→https sur une page HTTPS
 * (UPT-1505) et sockJsTransports() exclut le WebSocket derrière SWA ainsi que
 * les transports iframe (UPT-1615).
 */

import { Client, ReconnectionTimeMode, type StompHeaders } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { sockJsTransports } from '@/uptk/core/api/sockJsTransports';
import { ensureFreshAccessToken } from '@/uptk/core/api/clerkTokenProvider';

/** Endpoint STOMP/SockJS du backend (sous /api pour passer le proxy SWA). */
export const STOMP_ENDPOINT_PATH = '/api/ws';
/** Délai initial de reconnexion (ms), doublé à chaque tentative. */
export const RECONNECT_DELAY_MS = 5000;
/** Plafond du délai de reconnexion (ms). */
export const MAX_RECONNECT_DELAY_MS = 60000;
/** Endpoint injoignable : abandon après ce nombre d'échecs consécutifs (UPT-1512). */
export const MAX_CONNECT_FAILURES = 3;
/** Trames ERROR du serveur (CONNECT ou abonnement refusé) : abandon après ce nombre de refus consécutifs. */
export const MAX_SERVER_REFUSALS = 5;
/** Marge de validité exigée du jeton au moment du CONNECT (ms). */
const TOKEN_FRESHNESS_MARGIN_MS = 15000;
/** Durée de connexion stable après laquelle le compteur de refus repart de zéro (ms). */
const STABLE_CONNECTION_MS = 30000;
const MS_PER_SECOND = 1000;

/**
 * En-têtes du CONNECT STOMP pour un jeton donné (fonction pure) :
 * `Authorization: Bearer <jeton>` — aucun en-tête sans jeton.
 */
export function buildStompConnectHeaders(token: string | null | undefined): StompHeaders {
  const normalized = typeof token === 'string' ? token.trim() : '';
  return normalized ? { Authorization: `Bearer ${normalized}` } : {};
}

/** Notifications de jobs du tenant (remplace l'ancien `/topic/jobs` global). */
export function jobsTopic(tenantId: string): string {
  return `/topic/jobs/${tenantId}`;
}

/** Signal de contexte dashboard d'un workspace (inchangé, UPT-1711). */
export function dashboardContextTopic(tenantId: string, workspaceId: string): string {
  return `/topic/dashboard/${tenantId}/${workspaceId}/context`;
}

/**
 * Délai (ms) avant la tentative n° `attempt` (1 = première relance) :
 * 5 s, 10 s, 20 s, 40 s puis plafond — même progression que le backoff
 * exponentiel natif de stompjs, réutilisée pour l'attente d'un jeton.
 */
export function reconnectDelayMs(attempt: number): number {
  const exponent = Math.max(0, Math.floor(attempt) - 1);
  return Math.min(RECONNECT_DELAY_MS * 2 ** exponent, MAX_RECONNECT_DELAY_MS);
}

/** Base URL du backend pour l'endpoint temps réel. */
export function backendBaseUrl(): string {
  const env = import.meta.env?.VITE_API_URL as string | undefined;
  const configured = env && env.trim() !== '' ? env.replace(/\/$/, '') : '';

  if (configured) {
    // When an explicit API URL is configured, coerce http:// → https:// if the
    // page is served over HTTPS. SockJS throws a SecurityError if its connection
    // URL is http:// on an HTTPS page (UPT-1505).
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return configured.replace(/^http:\/\//, 'https://');
    }
    return configured;
  }

  // No VITE_API_URL set — SWA deployment with linked-backend proxy.
  // Use the page origin so SockJS uses the same protocol as the page.
  // /api/ws is forwarded to the ACA by the SWA backend link (which proxies /api/*).
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'http://localhost:8080';
}

export type RealtimeClientOptions = {
  /** Préfixe des logs console (ex. `jobNotifications`). */
  name: string;
  /** Appelé à chaque (re)connexion établie — les abonnements de la session précédente sont perdus : (ré)abonner ici. */
  onConnect: (stomp: Client) => void;
  /** Appelé quand une connexion établie tombe. */
  onDisconnect?: () => void;
};

export type RealtimeClient = {
  /** Client STOMP sous-jacent (abonnements). */
  readonly stomp: Client;
  /** Ouvre la connexion si nécessaire (idempotent) ; relance immédiatement un essai en attente de jeton. */
  ensureConnected: () => void;
  /** Connexion STOMP établie (CONNECTED reçu). */
  isConnected: () => boolean;
  /** Abandon pour la session (endpoint injoignable ou refus répétés du serveur). */
  hasGivenUp: () => boolean;
};

export function createRealtimeClient(options: RealtimeClientOptions): RealtimeClient {
  const tag = `[${options.name}]`;
  let connected = false;
  /** Un CONNECTED a été reçu sur le socket courant (distinct de `connected`, remis à faux dès le DISCONNECT). */
  let sessionEstablished = false;
  let gaveUp = false;
  /** Sockets fermés sans CONNECTED ni ERROR (endpoint injoignable). */
  let connectFailures = 0;
  /** Trames ERROR consécutives (CONNECT refusé, abonnement refusé). */
  let serverRefusals = 0;
  /** Une trame ERROR a précédé la fermeture de socket en cours. */
  let refusedByServer = false;
  let noTokenAttempts = 0;
  let tokenRetryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Relance programmée après un refus du serveur (non court-circuitable par ensureConnected). */
  let refusalRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let stableTimer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
    if (timer) clearTimeout(timer);
  };

  const giveUp = (reason: string) => {
    gaveUp = true;
    clearTimer(tokenRetryTimer);
    tokenRetryTimer = null;
    clearTimer(refusalRetryTimer);
    refusalRetryTimer = null;
    console.warn(
      `${tag} ${reason} — temps réel désactivé pour cette session (recharger la page pour réessayer)`
    );
    void stomp.deactivate();
  };

  const scheduleTokenRetry = () => {
    if (tokenRetryTimer || gaveUp) return;
    tokenRetryTimer = setTimeout(() => {
      tokenRetryTimer = null;
      stomp.activate();
    }, reconnectDelayMs(noTokenAttempts));
  };

  const scheduleRefusalRetry = () => {
    if (refusalRetryTimer || gaveUp) return;
    // Reconnexion pilotée ici : le client est mis en retrait (aucune relance
    // native), puis réactivé après un délai croissant avec le nombre de refus.
    void stomp.deactivate();
    refusalRetryTimer = setTimeout(() => {
      refusalRetryTimer = null;
      if (!gaveUp) stomp.activate();
    }, reconnectDelayMs(serverRefusals));
  };

  const stomp = new Client({
    webSocketFactory: () =>
      new SockJS(`${backendBaseUrl()}${STOMP_ENDPOINT_PATH}`, undefined, {
        transports: sockJsTransports(),
      }),
    reconnectDelay: RECONNECT_DELAY_MS,
    maxReconnectDelay: MAX_RECONNECT_DELAY_MS,
    reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
    // Jeton obtenu AVANT chaque CONNECT (première connexion et chaque
    // reconnexion) — jamais mis en cache ici, Clerk le renouvelle (~60 s).
    beforeConnect: async (client) => {
      let token: string | null = null;
      try {
        token = await ensureFreshAccessToken({ marginMs: TOKEN_FRESHNESS_MARGIN_MS });
      } catch (error) {
        console.warn(`${tag} jeton d'accès indisponible`, error);
      }
      client.connectHeaders = buildStompConnectHeaders(token);
      if (!token) {
        // Pas de CONNECT anonyme (refus garanti) : le client se met en retrait
        // (stompjs n'ouvre pas le socket quand il est inactif) et réessaie
        // après un délai croissant.
        noTokenAttempts += 1;
        await client.deactivate();
        scheduleTokenRetry();
      }
    },
    onConnect: () => {
      connected = true;
      sessionEstablished = true;
      connectFailures = 0;
      noTokenAttempts = 0;
      refusedByServer = false;
      clearTimer(stableTimer);
      stableTimer = setTimeout(() => {
        serverRefusals = 0;
      }, STABLE_CONNECTION_MS);
      options.onConnect(stomp);
    },
    onDisconnect: () => {
      connected = false;
    },
    onStompError: (frame) => {
      const message = frame.headers['message'] ?? '';
      refusedByServer = true;
      serverRefusals += 1;
      const stage = connected ? 'abonnement refusé' : 'CONNECT refusé';
      const nextDelayS = reconnectDelayMs(serverRefusals) / MS_PER_SECOND;
      console.warn(
        `${tag} ${stage} par le serveur (${message || 'jeton absent ou invalide'}) — ` +
        `nouvelle tentative avec un jeton frais dans ${nextDelayS} s au plus`
      );
    },
    onWebSocketClose: () => {
      const established = sessionEstablished;
      sessionEstablished = false;
      connected = false;
      clearTimer(stableTimer);
      stableTimer = null;
      if (gaveUp) return;
      if (established) options.onDisconnect?.();
      if (refusedByServer) {
        refusedByServer = false;
        if (serverRefusals >= MAX_SERVER_REFUSALS) {
          giveUp(`${MAX_SERVER_REFUSALS} refus consécutifs du serveur`);
        } else {
          scheduleRefusalRetry(); // beforeConnect refera un jeton frais
        }
        return;
      }
      if (established) return; // coupure d'une connexion établie : reconnexion normale
      connectFailures += 1;
      if (connectFailures >= MAX_CONNECT_FAILURES) {
        giveUp(`${STOMP_ENDPOINT_PATH} injoignable après ${MAX_CONNECT_FAILURES} tentatives (UPT-1512)`);
      }
    },
  });

  return {
    stomp,
    ensureConnected: () => {
      if (gaveUp) return;
      if (tokenRetryTimer) {
        // Un abonné arrive (job lancé, page dashboard) : on n'attend pas la fin du délai.
        clearTimer(tokenRetryTimer);
        tokenRetryTimer = null;
      }
      // Après un refus du serveur, le délai croissant est respecté (pas de boucle serrée).
      if (!stomp.active && !refusalRetryTimer) stomp.activate();
    },
    isConnected: () => connected,
    hasGivenUp: () => gaveUp,
  };
}
